import { Component, inject, signal, ViewChild, AfterViewInit, Output, EventEmitter, input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { map, switchMap } from 'rxjs/operators';

// Kendo Components
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SVGIcon, playIcon, pencilIcon, trashIcon } from '@progress/kendo-svg-icons';

// Core Components
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { PopupHostComponent } from 'src/app/core/components/popup-host.component';

// Services
import { ScheduledReportService, ScheduledReport, ReportJobConfigBase } from 'src/app/core/services/reporting/scheduled-report.service';
import { NotificationService } from '@progress/kendo-angular-notification';

// Popup Components
import { CustomerNamesPopupComponent } from './components/customer-names-popup.component';


/**
 * Scheduled Component
 * Displays and manages recurring report schedules using datatables
 */
@Component({
  selector: 'app-reporting-scheduled',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsModule, DatatableComponent],
  templateUrl: './scheduled.component.html',
  styleUrls: ['./scheduled.component.scss'],
})
export class ScheduledComponent extends PopupHostComponent implements AfterViewInit {
  @ViewChild('scheduledTable') scheduledTable!: DatatableComponent;

  private scheduledReportService = inject(ScheduledReportService);
  private notificationService = inject(NotificationService);

  @Output() newSchedule = new EventEmitter<void>();

  onNewScheduleClick() {
    this.newSchedule.emit();

  }

  // Datatable configuration
  public datatableUrl = `${environment.apiUrl}/v2/reporting/api/report-schedules/`;
  public totalCount = signal<number>(0);
  public initialFilters: any = {};
  public organizationId = input<number | null>(null);

  private deletingIds = new Set<number>();

  // Icons for action buttons
  public playIcon: SVGIcon = playIcon;
  public pencilIcon: SVGIcon = pencilIcon;
  public trashIcon: SVGIcon = trashIcon;

  // Filter state
  public frequencyFilter: string = 'all';

  // Filter options
  public frequencyOptions = [
    { value: 'all', label: 'All Frequencies' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  // Datatable columns configuration
  public datatableColumns = [
    {
      data: 'name',
      name: 'name',
      title: 'Report Type',
      type: 'string',
      render: (data: any, type: any, row: any) => {
        const customerCount = row.organizations_names ? row.organizations_names.length : 0;
        const customerText = customerCount === 1 ? `${customerCount} customer` : `${customerCount} customers`;
        // Escape single quotes in JSON to avoid breaking the HTML attribute
        const customerNamesJson = JSON.stringify(row.organizations_names || []).replace(/'/g, '&#39;');

        return `
          <div>
            <div class="fw-semibold">${data}</div>
            <div class="text-muted small d-flex align-items-center gap-1">
              <span>${customerText}</span>
              <button
                class="btn btn-sm btn-link p-0 text-primary action-view-customers"
                data-id="${row.id}"
                data-customer-names='${customerNamesJson}'
                title="View customer names"
                style="font-size: 0.875rem; line-height: 1; text-decoration: none;">
                👁️
              </button>
            </div>
          </div>
        `;
      },
    },
    {
      data: 'created_by_obj.email',
      name: 'created_by',
      title: 'Scheduled By',
      type: 'string',
      render: (data: string) => {
        if (!data) return '<span class="text-muted">—</span>';
        return `<div class="small">${data}</div>`;
      },
    },
    {
      data: 'created_at',
      name: 'created_at',
      title: 'Date Created',
      type: 'string',
      render: (data: string) => {
        if (!data) return '<span class="text-muted">—</span>';
        const date = new Date(data);
        const dateStr = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        });
        return `
          <div class="small">
            <div>${dateStr}</div>
            <div class="text-muted">${timeStr}</div>
          </div>
        `;
      },
    },
    {
      data: 'schedule_type',
      name: 'schedule_type',
      title: 'Frequency',
      type: 'select',
      choices: [
        ['daily', 'Daily'],
        ['weekly', 'Weekly'],
        ['monthly', 'Monthly'],
      ],
      render: (data: any) => {
        const colorMap: any = {
          daily: 'success',
          weekly: 'primary',
          monthly: 'info',
        };
        const color = colorMap[data] || 'secondary';
        return `<span class="badge bg-${color}">${data}</span>`;
      },
    },
    {
      data: 'next_run_at',
      name: 'next_run_at',
      title: 'Next Run',
      type: 'string',
      render: (data: string) => {
        if (!data) return '<span class="text-muted">—</span>';
        const date = new Date(data);
        const dateStr = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        });
        return `
          <div class="small">
            <div>${dateStr}</div>
            <div class="text-muted">${timeStr}</div>
          </div>
        `;
      },
    },
    {
      data: 'updated_at',
      name: 'updated_at',
      title: 'Last Run',
      type: 'string',
      render: (data: string) => {
        if (!data) return '<span class="text-muted">—</span>';
        const date = new Date(data);
        return `<div class="small">${date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}</div>`;
      },
    },
    {
      data: 'id',
      name: 'actions',
      title: 'Actions',
      type: 'actions',
      orderable: false,
      render: (data: string, type: any, row: any) => {
        return `
          <div class="btn-group btn-group-sm" role="group">
            <button class="btn btn-sm btn-outline-primary action-run" data-id="${data}" title="Run Now">
              ▶
            </button>
            <button class="btn btn-sm btn-outline-danger action-delete" data-id="${data}" title="Delete">
              🗑️
            </button>
          </div>
        `;
      },
    },
  ];

  ngAfterViewInit(): void {
    // Set up datatable event handlers after view init
    setTimeout(() => {
      this.setupDatatableEventHandlers();
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      this.clearFilters();
      this.scheduledTable?.reload();
    }
  }

  /**
   * Setup event handlers for datatable action buttons
   */
  private setupDatatableEventHandlers(): void {
    // Remove existing handlers first
    $(document).off('click', '.action-run');
    $(document).off('click', '.action-edit');
    $(document).off('click', '.action-delete');
    $(document).off('click', '.action-view-customers');

    // Use jQuery event delegation since datatable regenerates rows
    $(document).on('click', '.action-run', (event) => {
      const scheduleId = $(event.currentTarget).data('id');
      this.runScheduleNow(scheduleId);
    });

    $(document).on('click', '.action-edit', (event) => {
      const scheduleId = $(event.currentTarget).data('id');
      this.editSchedule(scheduleId);
    });

    $(document).on('click', '.action-delete', (event) => {
      const scheduleId = $(event.currentTarget).data('id');
      this.deleteSchedule(scheduleId);
    });

    $(document).on('click', '.action-view-customers', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const raw = $(event.currentTarget).attr('data-customer-names');
      const customerNames = raw ? JSON.parse(raw) : [];
      this.viewCustomerNames(customerNames);
    });
  }

  /**
   * Handle filter changes
   */
  public onFilterChange(): void {
    const odataConditions: string[] = [];

    if (this.frequencyFilter !== 'all') {
      odataConditions.push(`schedule_type eq '${this.frequencyFilter}'`);
    }

    // Update datatable filters
    const odataFilter = odataConditions.join(' and ');

    if (this.scheduledTable) {
      this.scheduledTable.setFilters({
        $filter: odataFilter
      });
    }
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.frequencyFilter = 'all';

    if (this.scheduledTable) {
      this.scheduledTable.setFilters({});
    }
  }

  private hasTime(dateStr: string): boolean {
    return /T\d{2}:\d{2}(:\d{2})?/.test(dateStr) || /\d{2}:\d{2}/.test(dateStr);
  }

  private previousPeriodFromRange(
    startDate: string,
    endDate: string,
    periodType: 'monthly' | 'weekly'
  ): { start: string; end: string } {
    const start = this.hasTime(startDate)
      ? new Date(startDate)
      : new Date(`${startDate}T00:00:00`);

    const end = this.hasTime(endDate)
      ? new Date(endDate)
      : new Date(`${endDate}T23:59:59`);

    let startPrev: Date;
    let endPrev: Date;

    if (periodType === 'monthly') {
      const year = start.getMonth() === 0 ? start.getFullYear() - 1 : start.getFullYear();
      const month = start.getMonth() === 0 ? 11 : start.getMonth() - 1;
      startPrev = new Date(year, month, 1, 0, 0, 0, 0);
      endPrev = new Date(year, month + 1, 0, 23, 59, 59, 999);

    } else if (periodType === 'weekly') {
      const dayOfWeek = start.getDay();
      const daysFromMonday = (dayOfWeek + 6) % 7;

      startPrev = new Date(start);
      startPrev.setDate(start.getDate() - daysFromMonday - 7);
      startPrev.setHours(0, 0, 0, 0);

      endPrev = new Date(startPrev);
      endPrev.setDate(startPrev.getDate() + 6);
      endPrev.setHours(23, 59, 59, 999);
    } else {
      throw new Error("Invalid period type. Must be 'monthly' or 'weekly'.");
    }

    return {
      start: formatDate(startPrev, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      end: formatDate(endPrev, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
    };
  }


  /**
   * Run schedule now
   */
  public runScheduleNow(scheduleId: number): void {
    this.scheduledReportService.getScheduleReport(scheduleId).pipe(
      map(config => {
        const { start, end } = this.previousPeriodFromRange(config.start_date, config.end_date, config.schedule_type);
        const { schedule_type, ...restConfig } = config;

          const jobConfig: ReportJobConfigBase = {
            ...restConfig,
            start_date: start,
            end_date: end
          };

        return jobConfig;
      }),
      switchMap((jobConfig: ReportJobConfigBase) =>
        this.scheduledReportService.runScheduledReportNow(jobConfig)
      )
    ).subscribe({
      next: (job) => {
        this.notificationService.show({
          content: 'Report schedule executed',
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
      },
      error: (error) => console.error('Error executing schedule:', error)
    });
  }

  /**
   * Edit schedule
   */
  public editSchedule(scheduleId: string): void {
    console.log('Edit schedule:', scheduleId);
    // TODO: Navigate to edit schedule page or open dialog
  }

  /**
   * Delete schedule
   */
  public deleteSchedule(scheduleId: number): void {
    // Avoid double delete request
    if (this.deletingIds.has(scheduleId)) {
      return;
    }

    if (!confirm('Are you sure you want to delete this scheduled report?')) {
      return;
    }

    this.deletingIds.add(scheduleId);

    this.scheduledReportService.deleteScheduledReport(scheduleId).subscribe({
      next: () => {
        // Reload datatable after deletion
        if (this.scheduledTable) {
          this.scheduledTable.setFilters(this.scheduledTable.filters());
          this.deletingIds.delete(scheduleId);
        }
      },
      error: (error) => {
        console.error('Error deleting schedule:', error);
        this.deletingIds.delete(scheduleId);
      },
    });
  }

  /**
   * View customer names popup
   */
  public viewCustomerNames(customerNames: string[]): void {
    if (!Array.isArray(customerNames)) {
      try {
        customerNames = JSON.parse(customerNames);
      } catch {
        customerNames = [];
      }
    }

    const options = {
      animate: { type: 'slide', direction: 'left', duration: 200 },
      anchorAlign: { horizontal: 'right', vertical: 'top' },
      popupAlign: { horizontal: 'right', vertical: 'top' },
    };

    this.openPopup(this.bodyElement, CustomerNamesPopupComponent, options);

    setTimeout(() => {
      const popupComponent = this.popupRef?.content.instance as CustomerNamesPopupComponent;
      if (popupComponent) {
        popupComponent.customerNames = customerNames;
        popupComponent.close.subscribe(() => {
          this.closePopup();
        });
      }
    });
  }
}
